import React, { useState } from 'react';

export const MAX_BATCH_SIZE = 50;

export const SCORE_THRESHOLD = 0.8;

/**
 * determine if a line is considered "useful" based on matchedData
 * @param {Object|null} matchedData 
 * @returns {boolean}
 */
export const isLineUseful = (matchedData) => {
    if (!matchedData) return false;
    return matchedData.predicted_label === 'fi' && matchedData.predicted_score >= SCORE_THRESHOLD;
};

/**
 * Get all lines from predictorData for fetching line scores
 * @param {Object} predictorData - predictor data with line scores
 * @returns {string} - lines concatenated with newline
 */
export const getLinesToScore = (predictorData) => {
    if (
        !predictorData || 
        !predictorData.details ||
        !predictorData.details.lines ||
        !predictorData.details.lines.length
    ) {
        return '';
    }
    return predictorData.details.lines.map(item => item.line).join('\n');
};

/**
 * check if a line is in the batch
 * @param {string} line 
 * @param {Object} batchData - { usefulToNotUseful: [], notUsefulToUseful: [] }
 * @returns {boolean}
 */
export const isLineInBatch = (line, batchData) => {
    if (!batchData) return false;
    const normalizedLine = line.trim().toLowerCase();
    const checkLine = item => item.line.trim().toLowerCase() === normalizedLine;
    const inUsefulToNotUseful = batchData.usefulToNotUseful && batchData.usefulToNotUseful.some(checkLine);
    const inNotUsefulToUseful = batchData.notUsefulToUseful && batchData.notUsefulToUseful.some(checkLine);
    return inUsefulToNotUseful || inNotUsefulToUseful;
};

/**
 * get the current usefulness based on lineScoreData (if available) or fallback to predictorData
 * @param {Object|null} matchedData - from predictorData
 * @param {Object|null} lineScoreData - result from fetchLineScore API
 * @param {string} line - the line text
 * @returns {boolean}
 */
const getCurrentUseful = (matchedData, lineScoreData, line) => {
    if (!matchedData) return false;
    
    if (lineScoreData) {
        const trimmedLine = line.trim();
        const fetchedScore = lineScoreData[trimmedLine];
        if (fetchedScore !== undefined) {
            return fetchedScore === 1;
        }
    }
    
    return isLineUseful(matchedData);
};

/**
 * renders lines with all "usefulness" conditions
 * @param {{
 *  line: string, 
 *  matchedData?: { predicted_score: number, predicted_label: string },
 *  lineScoreData?: Object|null,
 *  batchData?: Object|null,
 *  onLineClick?: (ev: React.MouseEvent, line: string, matchedData: Object|null) => void,
 * }} param0 
 */
const UsefulLine = ({ 
    line, 
    matchedData, 
    lineScoreData,
    batchData,
    onLineClick, 
}) => {
    const [isHovered, setIsHovered] = useState(false);

    let borderLeftColor = 'transparent';
    if (matchedData) {
        const isHighScore = matchedData.predicted_label === 'fi' && matchedData.predicted_score >= SCORE_THRESHOLD;
        borderLeftColor = isHighScore ? '#4CAF50' : '#F44336';
    }

    const isInBatch = isLineInBatch(line, batchData);
    
    const currentIsUseful = getCurrentUseful(matchedData, lineScoreData, line);
    
    let backgroundColor = 'transparent';
    let hoverBackgroundColor = 'rgba(0, 0, 0, 0.05)';

    if (matchedData) {
        backgroundColor = currentIsUseful 
            ? 'rgba(76, 175, 80, 0.25)' // fi - green
            : 'rgba(244, 67, 54, 0.15)'; // oi - red
        hoverBackgroundColor = currentIsUseful 
            ? 'rgba(76, 175, 80, 0.35)' 
            : 'rgba(244, 67, 54, 0.25)';
    }

    const isClickable = !!matchedData;

    const originalIsUseful = isLineUseful(matchedData);
    const hasDiscrepancy = matchedData && originalIsUseful !== currentIsUseful;

    let title = '';
    if (matchedData) {
        const scoreText = `Score: ${(matchedData.predicted_score * 100).toFixed(1)}%`;
        const labelText = `Label: ${matchedData.predicted_label}`;
        const batchText = isInBatch ? ' | IN BATCH (click to remove)' : '';
        let discrepancyText = '';
        if (hasDiscrepancy) {
            const fromType = originalIsUseful ? 'fi' : 'oi';
            const toType = currentIsUseful ? 'fi' : 'oi';
            discrepancyText = ` | CHANGED: ${fromType} → ${toType}`;
        }
        title = `${scoreText} | ${labelText}${discrepancyText}${batchText}`;
    }

    const handleClick = (ev) => {
        if (isClickable && onLineClick) {
            onLineClick(ev, line, matchedData);
        }
    };

    return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
            style={{
                backgroundColor: isHovered ? hoverBackgroundColor : backgroundColor,
                borderLeft: `3px solid ${borderLeftColor}`,
                borderRight: isInBatch ? '3px solid #9C27B0' : 'none',
                paddingLeft: '8px',
                paddingRight: isInBatch ? '5px' : '8px',
                margin: '2px 0',
                cursor: isClickable ? 'pointer' : 'default',
                transition: 'background-color 0.2s ease',
                fontStyle: isInBatch ? 'italic' : 'normal',
                opacity: isInBatch ? 0.8 : 1,
            }}
            title={title}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={handleClick}
        >
            {isInBatch && <span style={{ marginRight: '4px' }}>✓</span>}
            {line || '\u00A0'}
        </div>
    );
};

/**
 * get all discrepancy lines (where original prediction differs from current score)
 * @param {Object} predictorData - predictor data with line scores
 * @param {Object|null} lineScoreData - result from fetchLineScore API
 * @param {string} textLetter - letter text
 * @returns {{ fiToOi: string[], oiToFi: string[] }}
 */
export const getDiscrepancyLines = (predictorData, lineScoreData, textLetter) => {
    const result = { fiToOi: [], oiToFi: [] };
    
    if (
        !predictorData || 
        !predictorData.details ||
        !predictorData.details.lines ||
        !predictorData.details.lines.length ||
        !lineScoreData
    ) {
        return result;
    }

    const linesMap = new Map(predictorData.details.lines.map(item => [item.line.toLowerCase(), item]));
    const lines = textLetter.split('\n');

    lines.forEach((line) => {
        const trimmedLine = line.trim();
        const matchedData = linesMap.get(trimmedLine.toLowerCase());
        
        if (!matchedData) return;

        const originalIsUseful = isLineUseful(matchedData);
        const fetchedScore = lineScoreData[trimmedLine];
        
        if (fetchedScore === undefined) return;
        
        const currentIsUseful = fetchedScore === 1;
        
        if (originalIsUseful !== currentIsUseful) {
            if (originalIsUseful) {
                result.fiToOi.push(trimmedLine);
            } else {
                result.oiToFi.push(trimmedLine);
            }
        }
    });

    return result;
};

/**
 * renders letter text with useful line highlighting and click handlers
 * @param {Object} predictorData - predictor data with line scores
 * @param {string} textLetter - letter text
 * @param {(ev: React.MouseEvent, line: string, matchedData: Object|null) => void} onLineClick
 * @param {Object|null} lineScoreData - result from fetchLineScore API (keys are lines, values are 0 or 1)
 * @param {Object|null} batchData - batch data from context { usefulToNotUseful: [], notUsefulToUseful: [] }
 */
export const renderUsefulLines = (
    predictorData, 
    textLetter, 
    onLineClick, 
    lineScoreData = null,
    batchData = null,
) => {
    if (
        !predictorData || 
        !predictorData.details ||
        !predictorData.details.lines ||
        !predictorData.details.lines.length
    ) {
        return <span>{textLetter}</span>;
    }

    const linesMap = new Map(predictorData.details.lines.map(item => [item.line.toLowerCase(), item]));

    const lines = textLetter.split('\n');

    return lines.map((line, idx) => {
        const matchedData = linesMap.get(line.trim().toLowerCase());

        return (
            <UsefulLine 
                key={idx}
                line={line}
                matchedData={matchedData}
                lineScoreData={lineScoreData}
                batchData={batchData}
                onLineClick={onLineClick}
            />
        );
    });
};



// WEBPACK FOOTER //
// ./app/components/Letters/Single/Gazetter/Content/utils.jsx